if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.addEventListener('click', (event) => {
          const button = event.target.closest('button');
          const thumbnail = button?.closest('[data-target]');
          if (thumbnail) this.setActiveMedia(thumbnail.dataset.target, false);
        });
        if ((this.dataset.desktopLayout || '').includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        this.connectSwipe();
        if(this.dataset.sfGrid!=='true')return;
        this.gridQuery=window.matchMedia('(min-width: 990px)');
        this.gridChanged=()=>this.syncGridVisibility();
        this.gridQuery.addEventListener('change',this.gridChanged);
        this.syncGridVisibility();
      }

      disconnectedCallback() {
        this.swipeController?.abort();this.swipeController=null;
        this.gridQuery?.removeEventListener('change',this.gridChanged);
      }

      connectSwipe() {
        if(this.swipeController||!this.closest('.sf-pdp')||!this.elements.viewer)return;
        this.swipeController=new AbortController();const opts={signal:this.swipeController.signal};
        const viewer=this.elements.viewer;let start=null,dragged=false;
        viewer.addEventListener('touchstart',event=>{
          if(window.matchMedia('(min-width: 990px)').matches||event.touches.length!==1||event.target.closest('video,iframe,.deferred-media,product-model')){start=null;return;}
          start={x:event.touches[0].clientX,y:event.touches[0].clientY};dragged=false;
        },{...opts,passive:true});
        viewer.addEventListener('touchmove',event=>{
          if(!start||event.touches.length!==1){start=null;return;}
          const dx=event.touches[0].clientX-start.x,dy=event.touches[0].clientY-start.y;
          if(Math.abs(dy)>Math.abs(dx)&&Math.abs(dy)>12){start=null;return;}
          if(Math.abs(dx)>12&&Math.abs(dx)>Math.abs(dy)*1.25){dragged=true;if(event.cancelable)event.preventDefault();}
        },{...opts,passive:false});
        viewer.addEventListener('touchend',event=>{
          if(!start)return;
          const point=event.changedTouches[0],dx=point.clientX-start.x,dy=point.clientY-start.y;start=null;
          if(Math.abs(dx)<35||Math.abs(dx)<Math.abs(dy)*1.25)return;
          const items=[...viewer.querySelectorAll('li[data-media-id]')];if(items.length<2)return;
          const index=Math.max(0,items.findIndex(item=>item.classList.contains('is-active')));
          this.setActiveMedia(items[(index+(dx<0?1:-1)+items.length)%items.length].dataset.mediaId,false);
          this.suppressZoomUntil=Date.now()+400;
        },opts);
        viewer.addEventListener('touchcancel',()=>{start=null;dragged=false;},opts);
        viewer.addEventListener('click',event=>{if(dragged||Date.now()<(this.suppressZoomUntil||0)){event.preventDefault();event.stopPropagation();dragged=false;}},{...opts,capture:true});
      }

      syncGridVisibility() {
        if(this.dataset.sfGrid!=='true'||!this.elements.viewer)return;
        const items=[...this.elements.viewer.querySelectorAll('li[data-media-id]')];
        if(!items.some(item=>item.classList.contains('is-active')))items[0]?.classList.add('is-active');
        const grid=this.gridQuery?.matches ?? window.matchMedia('(min-width: 990px)').matches;
        items.forEach(item=>{const hidden=!grid&&!item.classList.contains('is-active');item.inert=hidden;item.setAttribute('aria-hidden',String(hidden));});
      }

      onSlideChanged(event) {
        if (this.closest('.sf-pdp')) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`li[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('li[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('li[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        if (this.closest('.sf-pdp')) {
          const track=activeMedia.parentElement;
          const grid=this.dataset.sfGrid==='true' && window.matchMedia('(min-width: 990px)').matches;
          track.scrollLeft=0;
          this.elements.viewer.querySelectorAll('li[data-media-id]').forEach(item=>{
            item.inert=!grid&&item!==activeMedia;
            item.setAttribute('aria-hidden',String(!grid&&item!==activeMedia));
          });
          activeMedia.querySelectorAll('img').forEach(img=>{img.loading='eager';});
          this.playActiveMedia(activeMedia);
          const thumbnail=this.elements.thumbnails?.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
          if(thumbnail)this.setActiveThumbnail(thumbnail);
          if(this.elements.liveRegion)this.elements.liveRegion.textContent=((window.SpotecT&&window.SpotecT['pdp.photo'])||'Photo')+' '+(thumbnail?.dataset.mediaPosition||'');
          this.dispatchEvent(new CustomEvent('sf:media-change',{bubbles:true}));
          return;
        }
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.closest('.sf-pdp') && this.mql.matches) {
          const list = this.elements.thumbnails.slider || this.elements.thumbnails.querySelector('.thumbnail-list');
          if (!list) return;
          const thumbRect = thumbnail.getBoundingClientRect();
          const listRect = list.getBoundingClientRect();
          if (thumbRect.top < listRect.top || thumbRect.bottom > listRect.bottom) {
            list.scrollTo({ top: list.scrollTop + thumbRect.top - listRect.top, behavior: 'auto' });
          }
          return;
        }
        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

        const list=this.elements.thumbnails.slider || this.elements.thumbnails.querySelector('.thumbnail-list');
        list?.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
